import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { BundleInfo, CapacitorUpdater } from "@capgo/capacitor-updater";

import { isNativeApp } from "./platform";

interface LiveUpdateManifest {
  version: string;
  url: string;
  checksum?: string;
}

const MANIFEST_URL = import.meta.env.VITE_LIVE_UPDATE_MANIFEST_URL;

export const isLiveUpdateEnabled =
  isNativeApp && Capacitor.getPlatform() === "android" && !!MANIFEST_URL;

export const compareVersion = (a: string, b: string): number => {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return Math.sign(diff);
  }
  return 0;
};

const splitVersion = (version: string) => {
  const parts = version.split(".");
  return {
    release: parts.slice(0, 3).join("."),
    build: parts.slice(3).join("."),
  };
};

export const isNewerVersion = (candidate: string, current: string): boolean => {
  const next = splitVersion(candidate);
  const running = splitVersion(current);
  const diff = compareVersion(next.release, running.release);
  return diff !== 0 ? diff > 0 : next.build !== running.build;
};

const fetchManifest = async (): Promise<LiveUpdateManifest | null> => {
  const res = await CapacitorHttp.get({
    url: MANIFEST_URL,
    headers: { "Cache-Control": "no-cache" },
  });
  if (res.status < 200 || res.status >= 300) return null;
  return (
    typeof res.data === "string" ? JSON.parse(res.data) : res.data
  ) as LiveUpdateManifest;
};

const findBundle = async (version: string) => {
  const { bundles } = await CapacitorUpdater.list();
  return bundles.find((b) => b.version === version);
};

export const checkForUpdate = async (): Promise<BundleInfo | null> => {
  const manifest = await fetchManifest();
  const currentVersion = import.meta.env.VITE_BUILD_VERSION;
  if (!manifest || !isNewerVersion(manifest.version, currentVersion)) {
    return null;
  }

  const existing = await findBundle(manifest.version);
  if (existing) {
    const reusable =
      existing.status === "pending" || existing.status === "success";
    return reusable ? existing : null;
  }
  return CapacitorUpdater.download({
    url: manifest.url,
    version: manifest.version,
    checksum: manifest.checksum,
  });
};

export const applyPendingUpdate = async (): Promise<boolean> => {
  const next = await CapacitorUpdater.getNextBundle();
  if (!next || next.status === "error") return false;
  const { bundle } = await CapacitorUpdater.current();
  if (next.id === bundle.id) return false;
  await CapacitorUpdater.set({ id: next.id });
  return true;
};

export const queueUpdate = async (): Promise<void> => {
  const bundle = await checkForUpdate();
  if (!bundle) return;
  await CapacitorUpdater.setMultiDelay({ delayConditions: [{ kind: "kill" }] });
  await CapacitorUpdater.next({ id: bundle.id });
};

export const runLiveUpdate = async (): Promise<void> => {
  if (!isLiveUpdateEnabled) return;
  if (await applyPendingUpdate()) return;
  await queueUpdate();
};
